import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @fileoverview TicketHistoryRow — LOCI ticket history row.
 *
 * 24px Lucide `Ticket` icon on the left, event title + `venue · MAR 14`
 * caption in the middle, mono price (or `Free`) on the right. Rendered as a
 * native `<a>` so Enter follows the link to the event detail screen.
 */
import { forwardRef } from 'react';
import { Ticket } from 'lucide-react';
import { useTheme } from '../../../theme';
// Candidate for a LOCI token — matches the design spec ("teal") pending
// palette expansion. Tracked inline so it's easy to grep when replaced.
const TICKET_ICON_COLOR = '#14B8A6';
const PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
});
const MONTH_ABBREVIATIONS = [
    'JAN',
    'FEB',
    'MAR',
    'APR',
    'MAY',
    'JUN',
    'JUL',
    'AUG',
    'SEP',
    'OCT',
    'NOV',
    'DEC',
];
function toDate(value) {
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}
function formatMediumDate(value) {
    const d = toDate(value);
    if (!d)
        return '';
    // Use UTC accessors so the rendered label aligns with the ISO `dateTime`
    // attribute emitted below (`toISOString().slice(0, 10)`) — otherwise a UTC
    // timestamp like `2026-03-14T01:00:00Z` would render `MAR 13` in US timezones
    // while the machine-readable `dateTime` says `2026-03-14`.
    return `${MONTH_ABBREVIATIONS[d.getUTCMonth()]} ${d.getUTCDate()}`;
}
function formatPrice(price) {
    if (!Number.isFinite(price) || price <= 0)
        return 'Free';
    return PRICE_FORMATTER.format(price);
}
function toIsoDate(value) {
    const d = toDate(value);
    return d ? d.toISOString().slice(0, 10) : undefined;
}
/**
 * `<TicketHistoryRow>` — anchor row used inside the Profile `TICKET HISTORY`
 * list. Exposes an aria-label summarizing the ticket so screen-reader users
 * get the title, venue, date, and price in a single announcement.
 */
export const TicketHistoryRow = forwardRef(({ id, title, venue, date, price, href, className, style, 'data-testid': dataTestId = 'ticket-history-row', }, ref) => {
    const theme = useTheme();
    const isFree = !Number.isFinite(price) || price <= 0;
    const priceLabel = formatPrice(price);
    const dateLabel = formatMediumDate(date);
    const isoDate = toIsoDate(date);
    const ariaLabel = `Open ticket for ${title} at ${venue} on ${dateLabel}, ${priceLabel}`;
    const rowStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing.md,
        width: '100%',
        padding: `${theme.spacing.sm} ${theme.spacing.xs}`,
        borderRadius: theme.borderRadius.sm,
        color: 'inherit',
        textDecoration: 'none',
        font: 'inherit',
    };
    const iconStyle = {
        width: 24,
        height: 24,
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: TICKET_ICON_COLOR,
    };
    const bodyStyle = {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    };
    const titleStyle = {
        margin: 0,
        fontFamily: theme.typography.fontFamilyDisplay ?? theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.body,
        fontWeight: theme.typography.fontWeight.semibold,
        color: theme.colors.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        letterSpacing: '-0.005em',
    };
    const captionStyle = {
        margin: 0,
        display: 'flex',
        alignItems: 'baseline',
        gap: 6,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.fontSize.small,
        color: theme.colors.textMuted,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };
    const dateStyle = {
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: '12px',
        letterSpacing: '0.04em',
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    const priceStyle = {
        fontFamily: theme.typography.fontFamilyMono,
        fontSize: theme.typography.fontSize.small,
        color: isFree ? theme.colors.textMuted : theme.colors.text,
        minWidth: 52,
        textAlign: 'right',
        flexShrink: 0,
        fontFeatureSettings: "'tnum' 1, 'zero' 1",
    };
    return (_jsxs("a", { ref: ref, href: href, "data-testid": dataTestId, "data-event-id": id, "aria-label": ariaLabel, className: className, style: { ...rowStyle, ...style }, children: [_jsx("span", { "aria-hidden": "true", style: iconStyle, "data-testid": "ticket-history-row-icon", children: _jsx(Ticket, { size: 24, strokeWidth: 1.75 }) }), _jsxs("span", { style: bodyStyle, children: [_jsx("span", { style: titleStyle, "data-testid": "ticket-history-row-title", children: title }), _jsxs("span", { style: captionStyle, "data-testid": "ticket-history-row-caption", children: [_jsx("span", { children: venue }), _jsx("span", { "aria-hidden": "true", children: ' · ' }), _jsx("time", { style: dateStyle, dateTime: isoDate, "data-testid": "ticket-history-row-date", children: dateLabel })] })] }), _jsx("span", { style: priceStyle, "data-testid": "ticket-history-row-price", children: priceLabel })] }));
});
TicketHistoryRow.displayName = 'TicketHistoryRow';
